import React, { Component } from "react";
import {
  Button, Input, Select, Tag, notification, Spin, Row, Col,
  Card, Avatar, Tooltip, Divider, Modal, Upload, Table,
  Tabs, Empty, Badge, Dropdown, Menu, Progress,
} from "antd";
import {
  UserOutlined, PlusOutlined, UploadOutlined, SearchOutlined,
  EditOutlined, DeleteOutlined, MailOutlined, PhoneOutlined,
  BellOutlined, CloseOutlined, DownOutlined, FilterOutlined,
  LinkedinOutlined, FacebookOutlined, InstagramOutlined,
  HomeOutlined, CalendarOutlined, HeartOutlined, StarOutlined,
  ThunderboltOutlined, CheckCircleOutlined, ImportOutlined,
  ExportOutlined, TeamOutlined, MoreOutlined,
} from "@ant-design/icons";
import { add, update, list, deleteRecord } from "../api/Apis";
import { withAppContext } from "../store/contextStore";

const { Option }    = Select;
const { TextArea }  = Input;
const { TabPane }   = Tabs;

// ── Brand tokens ──────────────────────────────────────────────────────────────
const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const GREEN  = "#2D6A4F";
const WHITE  = "#FFFFFF";
const BG     = "#F7F9FC";
const BORDER = "#E2E8F0";
const MUTED  = "#718096";
const RED    = "#E53E3E";

// ── Relationship types ────────────────────────────────────────────────────────
const RELATIONSHIP_TYPES = [
  { value: "lead",          label: "🎯 Lead",           color: "orange" },
  { value: "active_client", label: "🔥 Active Client",  color: "red"    },
  { value: "past_client",   label: "✅ Past Client",    color: "green"  },
  { value: "sphere",        label: "🤝 Sphere",         color: "blue"   },
  { value: "referral",      label: "⭐ Referral Source",color: "gold"   },
  { value: "vendor",        label: "🔧 Vendor",         color: "purple" },
  { value: "investor",      label: "💰 Investor",       color: "cyan"   },
  { value: "friend",        label: "👋 Friend",         color: "default"},
  { value: "family",        label: "❤️ Family",         color: "pink"   },
  { value: "other",         label: "📋 Other",          color: "default"},
];

// ── Life event types ──────────────────────────────────────────────────────────
const LIFE_EVENT_TYPES = [
  { value: "wedding",      label: "💍 Wedding / Engagement", signal: true  },
  { value: "baby",         label: "👶 New Baby / Pregnancy",  signal: true  },
  { value: "job_change",   label: "💼 Job Change / Promotion",signal: true  },
  { value: "divorce",      label: "⚖️ Divorce / Separation",  signal: true  },
  { value: "loss",         label: "🕊️ Loss / Bereavement",    signal: false },
  { value: "graduation",   label: "🎓 Graduation",            signal: true  },
  { value: "retirement",   label: "🏖️ Retirement",            signal: true  },
  { value: "relocation",   label: "📦 Moving / Relocation",   signal: true  },
  { value: "health",       label: "🏥 Health Event",          signal: false },
  { value: "anniversary",  label: "🎂 Anniversary",           signal: true  },
  { value: "milestone",    label: "🏆 Personal Milestone",    signal: true  },
  { value: "other",        label: "📌 Other",                 signal: false },
];

// ── Contact frequency options ─────────────────────────────────────────────────
const CONTACT_FREQUENCY = [
  { value: "weekly",     label: "Weekly"     },
  { value: "biweekly",   label: "Bi-weekly"  },
  { value: "monthly",    label: "Monthly"    },
  { value: "quarterly",  label: "Quarterly"  },
  { value: "biannual",   label: "Twice a year"},
  { value: "annual",     label: "Annually"   },
  { value: "as_needed",  label: "As needed"  },
];

// ── CSV field mapping ─────────────────────────────────────────────────────────
const CSV_FIELD_MAP = [
  { key: "firstName",      label: "First Name",       required: true  },
  { key: "lastName",       label: "Last Name",        required: false },
  { key: "email",          label: "Email",            required: false },
  { key: "phone",          label: "Phone",            required: false },
  { key: "birthday",       label: "Birthday",         required: false },
  { key: "address",        label: "Home Address",     required: false },
  { key: "city",           label: "City",             required: false },
  { key: "state",          label: "State",            required: false },
  { key: "zip",            label: "Zip Code",         required: false },
  { key: "employer",       label: "Employer",         required: false },
  { key: "jobTitle",       label: "Job Title",        required: false },
  { key: "notes",          label: "Notes",            required: false },
  { key: "relationshipType",label: "Relationship",    required: false },
  { key: "tags",           label: "Tags",             required: false },
  { key: "linkedin",       label: "LinkedIn URL",     required: false },
  { key: "facebook",       label: "Facebook URL",     required: false },
  { key: "instagram",      label: "Instagram URL",    required: false },
  { key: "homeAddress",    label: "Property Address", required: false },
  { key: "purchaseDate",   label: "Purchase Date",    required: false },
  { key: "purchasePrice",  label: "Purchase Price",   required: false },
  { key: "mortgageRate",   label: "Mortgage Rate",    required: false },
  { key: "spousePartner",  label: "Spouse/Partner",   required: false },
  { key: "children",       label: "Children",         required: false },
  { key: "hobbies",        label: "Hobbies",          required: false },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}
function daysUntilNextBirthday(birthdayStr) {
  if (!birthdayStr) return null;
  const today = new Date();
  const bday  = new Date(birthdayStr);
  const next  = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
  if (next < today) next.setFullYear(today.getFullYear() + 1);
  return Math.ceil((next - today) / 86400000);
}
function signalBadge(contact) {
  const signals = [];
  const lastContact = daysSince(contact.lastContactDate);
  const bdayDays    = daysUntilNextBirthday(contact.birthday);
  const annivDays   = contact.homeAnniversary
    ? daysUntilNextBirthday(contact.homeAnniversary) : null;

  if (bdayDays !== null && bdayDays <= 7)   signals.push({ icon: "🎂", label: "Birthday soon",   priority: "high"   });
  if (annivDays !== null && annivDays <= 14) signals.push({ icon: "🏡", label: "Home anniversary",priority: "medium" });
  if (lastContact !== null) {
    const freq = contact.contactFrequency || "quarterly";
    const thresholds = { weekly:7, biweekly:14, monthly:30, quarterly:90, biannual:180, annual:365, as_needed:999 };
    const threshold  = thresholds[freq] || 90;
    if (lastContact > threshold) signals.push({ icon: "⏰", label: `${lastContact}d since contact`, priority: lastContact > threshold * 2 ? "urgent" : "medium" });
  }
  return signals;
}

// ── Empty form template ───────────────────────────────────────────────────────
const EMPTY_FORM = () => ({
  // Basic
  firstName: "", lastName: "", email: "", phone: "",
  // Relationship
  relationshipType: "sphere", contactFrequency: "quarterly",
  lastContactDate: "", tags: [],
  // Personal
  birthday: "", spousePartner: "", children: "", hobbies: [],
  employer: "", jobTitle: "",
  notes: "",
  // Real estate
  currentHome: "", homeAnniversary: "", purchasePrice: "",
  mortgageRate: "", mortgageType: "", isRenter: false,
  lookingToBuy: false, lookingToSell: false,
  estimatedEquity: "",
  // Life events
  lifeEvents: [],
  // Social
  linkedin: "", facebook: "", instagram: "", twitter: "", tiktok: "",
  // Invite
  inviteSent: false, joinedPlatform: false,
});

// ── Hobby options ─────────────────────────────────────────────────────────────
const HOBBIES = [
  "Golf","Tennis","Hiking","Running","Cycling","Swimming","Skiing","Fishing",
  "Gardening","Cooking","Wine","Travel","Photography","Music","Reading",
  "Yoga","Fitness","Dogs","Horses","Boating","Hunting","Sports fan","Gaming",
  "Art","Volunteering","Church","Family","Investing",
];

// ── Tag suggestions ───────────────────────────────────────────────────────────
const TAG_SUGGESTIONS = [
  "referral_source","VIP","investor","repeat_buyer","relocating",
  "downsizing","upsizing","first_time_buyer","military","luxury",
  "follow_up","hot_lead","sphere_a","sphere_b","closed",
];

// ─────────────────────────────────────────────────────────────────────────────
// ContactForm — add / edit a contact
// ─────────────────────────────────────────────────────────────────────────────
function ContactForm({ form, onChange, onAddLifeEvent, onRemoveLifeEvent, onChangeLifeEvent }) {
  const setF = (field, val) => onChange(field, val);
  const toggleHobby = (h) => {
    const arr = form.hobbies || [];
    setF("hobbies", arr.includes(h) ? arr.filter(x => x !== h) : [...arr, h]);
  };
  const toggleTag = (t) => {
    const arr = form.tags || [];
    setF("tags", arr.includes(t) ? arr.filter(x => x !== t) : [...arr, t]);
  };

  const sectionTitle = (icon, label) => (
    <div style={{ fontSize: "11px", fontWeight: 800, color: NAVY, textTransform: "uppercase",
      letterSpacing: ".06em", display: "flex", alignItems: "center", gap: "6px",
      margin: "20px 0 12px", paddingBottom: "6px", borderBottom: `1px solid ${BORDER}` }}>
      <span>{icon}</span>{label}
    </div>
  );

  const field = (label, node, required = false) => (
    <div style={{ marginBottom: "14px" }}>
      <label style={{ fontSize: "11px", fontWeight: 700, color: NAVY, textTransform: "uppercase",
        letterSpacing: ".04em", display: "block", marginBottom: "5px" }}>
        {label}{required && <span style={{ color: RED }}> *</span>}
      </label>
      {node}
    </div>
  );

  const inp = (placeholder, fkey, type = "text") => (
    <Input type={type} placeholder={placeholder} value={form[fkey] || ""}
      onChange={e => setF(fkey, e.target.value)} style={{ borderRadius: "10px" }} />
  );

  return (
    <div style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: "4px" }}>

      {sectionTitle("👤", "Basic Information")}
      <Row gutter={[12, 0]}>
        <Col span={12}>{field("First Name *", inp("First name", "firstName"), true)}</Col>
        <Col span={12}>{field("Last Name",    inp("Last name",  "lastName"))}</Col>
        <Col span={12}>{field("Email",        inp("email@example.com", "email", "email"))}</Col>
        <Col span={12}>{field("Phone",        inp("(555) 555-5555", "phone", "tel"))}</Col>
        <Col span={12}>
          {field("Relationship", (
            <Select value={form.relationshipType || "sphere"} onChange={v => setF("relationshipType", v)}
              style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {RELATIONSHIP_TYPES.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
            </Select>
          ))}
        </Col>
        <Col span={12}>
          {field("Contact Frequency", (
            <Select value={form.contactFrequency || "quarterly"} onChange={v => setF("contactFrequency", v)}
              style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              {CONTACT_FREQUENCY.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
            </Select>
          ))}
        </Col>
      </Row>

      {sectionTitle("🎂", "Personal Details")}
      <Row gutter={[12, 0]}>
        <Col span={12}>{field("Birthday",         inp("", "birthday", "date"))}</Col>
        <Col span={12}>{field("Spouse / Partner", inp("Name", "spousePartner"))}</Col>
        <Col span={12}>{field("Children",         inp("e.g. 2 kids, ages 4 and 7", "children"))}</Col>
        <Col span={12}>{field("Employer",         inp("Company name", "employer"))}</Col>
        <Col span={12}>{field("Job Title",        inp("Their role", "jobTitle"))}</Col>
        <Col span={12}>{field("Last Contacted",   inp("", "lastContactDate", "date"))}</Col>
      </Row>

      {field("Hobbies & Interests", (
        <div>
          {HOBBIES.map(h => (
            <span key={h} onClick={() => toggleHobby(h)}
              style={{
                display: "inline-block", margin: "3px", padding: "4px 11px",
                borderRadius: "20px", fontSize: "12px", cursor: "pointer",
                background: (form.hobbies||[]).includes(h) ? NAVY : WHITE,
                color:      (form.hobbies||[]).includes(h) ? WHITE : "#333",
                border:     `1.5px solid ${(form.hobbies||[]).includes(h) ? NAVY : BORDER}`,
                transition: "all .15s",
              }}>
              {h}
            </span>
          ))}
        </div>
      ))}

      {sectionTitle("🏡", "Real Estate Context")}
      <Row gutter={[12, 0]}>
        <Col span={24}>{field("Current Home Address", inp("123 Main St, City, State", "currentHome"))}</Col>
        <Col span={12}>{field("Purchase Date", inp("", "homeAnniversary", "date"))}</Col>
        <Col span={12}>{field("Purchase Price", <Input prefix="$" placeholder="e.g. 450000" value={form.purchasePrice||""}
          onChange={e => setF("purchasePrice", e.target.value)} style={{ borderRadius: "10px" }} />)}
        </Col>
        <Col span={12}>{field("Mortgage Rate", <Input suffix="%" placeholder="e.g. 3.25" value={form.mortgageRate||""}
          onChange={e => setF("mortgageRate", e.target.value)} style={{ borderRadius: "10px" }} />)}
        </Col>
        <Col span={12}>
          {field("Status", (
            <Select value={
              form.lookingToBuy ? "buying" : form.lookingToSell ? "selling" : form.isRenter ? "renting" : "homeowner"
            } onChange={v => {
              setF("isRenter",       v === "renting");
              setF("lookingToBuy",   v === "buying");
              setF("lookingToSell",  v === "selling");
            }} style={{ width: "100%" }} getPopupContainer={t => t.parentNode}>
              <Option value="homeowner">🏠 Homeowner</Option>
              <Option value="renting">🏢 Renting</Option>
              <Option value="buying">🔍 Looking to Buy</Option>
              <Option value="selling">📋 Looking to Sell</Option>
            </Select>
          ))}
        </Col>
      </Row>

      {sectionTitle("📅", "Life Events")}
      {(form.lifeEvents||[]).map((evt, i) => (
        <div key={i} style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
          <Select value={evt.type} onChange={v => onChangeLifeEvent(i, "type", v)}
            style={{ width: "200px" }} getPopupContainer={t => t.parentNode} size="small">
            {LIFE_EVENT_TYPES.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
          </Select>
          <Input type="date" value={evt.date||""} onChange={e => onChangeLifeEvent(i, "date", e.target.value)}
            style={{ width: "140px", borderRadius: "8px" }} size="small" />
          <Input value={evt.notes||""} onChange={e => onChangeLifeEvent(i, "notes", e.target.value)}
            placeholder="Notes about this event" style={{ flex: 1, borderRadius: "8px" }} size="small" />
          <Button size="small" danger icon={<CloseOutlined />} onClick={() => onRemoveLifeEvent(i)}
            style={{ borderRadius: "50%", flexShrink: 0 }} />
        </div>
      ))}
      <Button size="small" icon={<PlusOutlined />} onClick={onAddLifeEvent}
        style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY, marginBottom: "8px" }}>
        Add Life Event
      </Button>

      {sectionTitle("🔗", "Social Media")}
      <Row gutter={[12, 0]}>
        <Col span={12}>{field("LinkedIn", <Input prefix={<LinkedinOutlined style={{ color: "#0077B5" }} />}
          placeholder="linkedin.com/in/username" value={form.linkedin||""}
          onChange={e => setF("linkedin", e.target.value)} style={{ borderRadius: "10px" }} />)}
        </Col>
        <Col span={12}>{field("Facebook", <Input prefix={<FacebookOutlined style={{ color: "#1877F2" }} />}
          placeholder="facebook.com/username" value={form.facebook||""}
          onChange={e => setF("facebook", e.target.value)} style={{ borderRadius: "10px" }} />)}
        </Col>
        <Col span={12}>{field("Instagram", <Input prefix={<InstagramOutlined style={{ color: "#E1306C" }} />}
          placeholder="@username" value={form.instagram||""}
          onChange={e => setF("instagram", e.target.value)} style={{ borderRadius: "10px" }} />)}
        </Col>
        <Col span={12}>{field("Twitter / X", inp("@username", "twitter"))}</Col>
      </Row>

      {sectionTitle("🏷️", "Tags")}
      <div style={{ marginBottom: "12px" }}>
        {TAG_SUGGESTIONS.map(t => (
          <span key={t} onClick={() => toggleTag(t)}
            style={{
              display: "inline-block", margin: "3px", padding: "4px 11px",
              borderRadius: "20px", fontSize: "11px", cursor: "pointer",
              background: (form.tags||[]).includes(t) ? GOLD : WHITE,
              color:      (form.tags||[]).includes(t) ? "#2A1600" : "#333",
              border:     `1.5px solid ${(form.tags||[]).includes(t) ? GOLD : BORDER}`,
            }}>
            #{t}
          </span>
        ))}
      </div>

      {sectionTitle("📝", "Notes")}
      {field("Private Notes (only you see these)", (
        <TextArea rows={4} value={form.notes||""}
          onChange={e => setF("notes", e.target.value)}
          placeholder="Anything you know about this person — family details, preferences, what they talked about, things to remember..."
          style={{ borderRadius: "10px" }} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CSV Import Modal
// ─────────────────────────────────────────────────────────────────────────────
class CSVImportModal extends Component {
  state = {
    step:         0,   // 0=upload, 1=map fields, 2=preview, 3=done
    csvRows:      [],
    csvHeaders:   [],
    fieldMapping: {},  // csvHeader → our field key
    preview:      [],
    importing:    false,
    imported:     0,
  };

  handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) { notification.error({ message: "CSV must have at least 2 rows" }); return; }
      const headers = lines[0].split(",").map(h => h.replace(/^"|"$/g,"").trim());
      const rows    = lines.slice(1).map(line => {
        const vals = line.split(",").map(v => v.replace(/^"|"$/g,"").trim());
        const obj  = {};
        headers.forEach((h, i) => { obj[h] = vals[i] || ""; });
        return obj;
      }).filter(r => Object.values(r).some(v => v));

      // Auto-map obvious headers
      const autoMap = {};
      headers.forEach(h => {
        const lower = h.toLowerCase().replace(/[\s_-]/g,"");
        const match = CSV_FIELD_MAP.find(f =>
          lower.includes(f.key.toLowerCase()) ||
          lower.includes(f.label.toLowerCase().replace(/[\s_-]/g,"")) ||
          (lower === "first" && f.key === "firstName") ||
          (lower === "last"  && f.key === "lastName")  ||
          (lower === "firstname" && f.key === "firstName") ||
          (lower === "lastname"  && f.key === "lastName")
        );
        if (match) autoMap[h] = match.key;
      });

      this.setState({ csvHeaders: headers, csvRows: rows, fieldMapping: autoMap, step: 1 });
    };
    reader.readAsText(file);
    return false; // prevent antd auto-upload
  };

  buildPreview = () => {
    const { csvRows, fieldMapping } = this.state;
    const preview = csvRows.slice(0, 5).map(row => {
      const contact = { ...EMPTY_FORM() };
      Object.entries(fieldMapping).forEach(([csvH, ourKey]) => {
        if (ourKey && row[csvH]) contact[ourKey] = row[csvH];
      });
      return contact;
    });
    this.setState({ preview, step: 2 });
  };

  doImport = async () => {
    const { csvRows, fieldMapping } = this.state;
    const { agentId, onImported } = this.props;
    this.setState({ importing: true, imported: 0 });
    let count = 0;
    for (const row of csvRows) {
      const contact = { ...EMPTY_FORM() };
      Object.entries(fieldMapping).forEach(([csvH, ourKey]) => {
        if (ourKey && row[csvH]) contact[ourKey] = row[csvH];
      });
      if (!contact.firstName) continue;
      try {
        await add("contacts", {
          entityData: {
            ...contact,
            agentId,
            recordType: "agentContact",
            createdAt:  new Date().toISOString(),
            updatedAt:  new Date().toISOString(),
            source:     "csv_import",
          }
        });
        count++;
      } catch(e) { console.warn("Import row failed:", e.message); }
    }
    this.setState({ importing: false, imported: count, step: 3 });
    if (onImported) onImported(count);
  };

  render() {
    const { visible, onClose } = this.props;
    const { step, csvHeaders, fieldMapping, preview, importing, imported, csvRows } = this.state;

    return (
      <Modal visible={visible} title="Import Contacts from CSV"
        onCancel={onClose} width={700}
        footer={null} className="modal-black-close"
        bodyStyle={{ padding: "20px 24px" }}>

        {/* Progress steps */}
        <div style={{ display: "flex", gap: "0", marginBottom: "24px" }}>
          {["Upload","Map Fields","Preview","Done"].map((s, i) => (
            <div key={s} style={{ flex: 1, textAlign: "center" }}>
              <div style={{
                width: "28px", height: "28px", borderRadius: "50%",
                background: i <= step ? NAVY : BORDER,
                color: i <= step ? WHITE : MUTED,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: 700, margin: "0 auto 4px",
              }}>
                {i < step ? <CheckCircleOutlined /> : i + 1}
              </div>
              <div style={{ fontSize: "11px", color: i === step ? NAVY : MUTED, fontWeight: i === step ? 700 : 400 }}>{s}</div>
            </div>
          ))}
        </div>

        {/* Step 0 — Upload */}
        {step === 0 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Upload.Dragger beforeUpload={this.handleFile} accept=".csv" showUploadList={false}
              style={{ borderRadius: "12px", borderColor: BORDER }}>
              <div style={{ padding: "30px 20px" }}>
                <UploadOutlined style={{ fontSize: "40px", color: NAVY, marginBottom: "12px", display: "block" }} />
                <div style={{ fontSize: "15px", fontWeight: 700, color: NAVY, marginBottom: "6px" }}>Drop your CSV file here</div>
                <div style={{ fontSize: "13px", color: MUTED, marginBottom: "12px" }}>or click to browse</div>
                <div style={{ fontSize: "12px", color: MUTED, background: BG, borderRadius: "8px", padding: "10px 16px", display: "inline-block" }}>
                  Supports any CSV export — from Excel, Google Contacts, Outlook, Follow Up Boss, LionDesk, etc.
                </div>
              </div>
            </Upload.Dragger>
            <div style={{ marginTop: "16px", fontSize: "12px", color: MUTED }}>
              Required: at least a <strong>First Name</strong> column. Everything else is optional.
            </div>
          </div>
        )}

        {/* Step 1 — Map fields */}
        {step === 1 && (
          <div>
            <div style={{ fontSize: "13px", color: MUTED, marginBottom: "16px" }}>
              Match your CSV columns to our fields. We've auto-matched what we could — adjust as needed.
            </div>
            <div style={{ maxHeight: "380px", overflowY: "auto" }}>
              <Row gutter={[8, 8]} style={{ marginBottom: "8px" }}>
                <Col span={10}><div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase" }}>Your CSV Column</div></Col>
                <Col span={14}><div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase" }}>Maps To</div></Col>
              </Row>
              {csvHeaders.map(h => (
                <Row key={h} gutter={[8, 8]} style={{ marginBottom: "8px", alignItems: "center" }}>
                  <Col span={10}>
                    <div style={{ background: BG, borderRadius: "8px", padding: "6px 10px", fontSize: "12px", fontWeight: 600, color: NAVY }}>{h}</div>
                  </Col>
                  <Col span={14}>
                    <Select value={fieldMapping[h] || undefined} onChange={v => this.setState(p => ({ fieldMapping: { ...p.fieldMapping, [h]: v } }))}
                      placeholder="— Skip this column —" allowClear style={{ width: "100%" }}
                      size="small" getPopupContainer={t => t.parentNode}>
                      {CSV_FIELD_MAP.map(f => <Option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</Option>)}
                    </Select>
                  </Col>
                </Row>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "16px" }}>
              <Button onClick={() => this.setState({ step: 0 })} style={{ borderRadius: "50px" }}>← Back</Button>
              <Button type="primary" onClick={this.buildPreview}
                style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY }}>
                Preview Import →
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — Preview */}
        {step === 2 && (
          <div>
            <div style={{ fontSize: "13px", color: MUTED, marginBottom: "12px" }}>
              Importing <strong style={{ color: NAVY }}>{csvRows.length} contacts</strong>. Here's a preview of the first 5:
            </div>
            <div style={{ maxHeight: "300px", overflowY: "auto", marginBottom: "16px" }}>
              {preview.map((c, i) => (
                <div key={i} style={{ display: "flex", gap: "12px", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${BORDER}` }}>
                  <Avatar size={36} style={{ background: NAVY, flexShrink: 0 }}>
                    {(c.firstName||"?").charAt(0).toUpperCase()}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>{c.firstName} {c.lastName}</div>
                    <div style={{ fontSize: "12px", color: MUTED }}>{c.email} {c.phone && `· ${c.phone}`}</div>
                  </div>
                  {c.birthday && <Tag style={{ borderRadius: "20px", fontSize: "10px" }}>🎂 {c.birthday}</Tag>}
                  {c.hobbies?.length > 0 && <Tag style={{ borderRadius: "20px", fontSize: "10px" }}>{c.hobbies[0]}</Tag>}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => this.setState({ step: 1 })} style={{ borderRadius: "50px" }}>← Back</Button>
              <Button type="primary" loading={importing} onClick={this.doImport}
                style={{ borderRadius: "50px", background: GREEN, borderColor: GREEN }}>
                {importing ? "Importing..." : `Import All ${csvRows.length} Contacts →`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3 — Done */}
        {step === 3 && (
          <div style={{ textAlign: "center", padding: "30px 20px" }}>
            <CheckCircleOutlined style={{ fontSize: "52px", color: GREEN, marginBottom: "16px", display: "block" }} />
            <div style={{ fontSize: "20px", fontWeight: 800, color: NAVY, marginBottom: "8px" }}>
              {imported} Contacts Imported! 🎉
            </div>
            <div style={{ fontSize: "14px", color: MUTED, marginBottom: "24px" }}>
              Your contacts have been added. The Home Buddha Assistant will start generating signals for them automatically.
            </div>
            <Button type="primary" onClick={onClose}
              style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontWeight: 700 }}>
              View My Contacts →
            </Button>
          </div>
        )}
      </Modal>
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ContactCard — single contact in the list view
// ─────────────────────────────────────────────────────────────────────────────
function ContactCard({ contact, onEdit, onDelete, onInvite, onContact }) {
  const relType  = RELATIONSHIP_TYPES.find(r => r.value === contact.relationshipType);
  const signals  = signalBadge(contact);
  const initials = `${(contact.firstName||"?").charAt(0)}${(contact.lastName||"").charAt(0)}`.toUpperCase();
  const lastContactDays = daysSince(contact.lastContactDate);

  return (
    <Card style={{ borderRadius: "14px", border: `1px solid ${BORDER}`, marginBottom: "10px",
      borderLeft: signals.some(s => s.priority === "urgent") ? `4px solid ${RED}` :
                  signals.some(s => s.priority === "high")   ? `4px solid ${GOLD}` :
                  signals.length > 0 ? `4px solid ${NAVY}` : `1px solid ${BORDER}` }}
      bodyStyle={{ padding: "14px 18px" }}>
      <Row align="middle" gutter={[12, 0]}>
        <Col style={{ flexShrink: 0 }}>
          <Badge count={signals.length} size="small" offset={[-4, 4]}
            style={{ background: signals.some(s=>s.priority==="urgent") ? RED : GOLD }}>
            <Avatar size={46} style={{ background: contact.joinedPlatform ? GREEN : NAVY, fontWeight: 700 }}>
              {initials}
            </Avatar>
          </Badge>
        </Col>
        <Col flex="1" style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "2px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "14px", fontWeight: 700, color: NAVY }}>
              {contact.firstName} {contact.lastName}
            </span>
            {relType && (
              <Tag color={relType.color} style={{ margin: 0, borderRadius: "20px", fontSize: "10px" }}>
                {relType.label}
              </Tag>
            )}
            {contact.joinedPlatform && (
              <Tag color="green" style={{ margin: 0, borderRadius: "20px", fontSize: "10px" }}>✅ On Platform</Tag>
            )}
            {contact.inviteSent && !contact.joinedPlatform && (
              <Tag color="default" style={{ margin: 0, borderRadius: "20px", fontSize: "10px" }}>📧 Invited</Tag>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            {contact.email && <span style={{ fontSize: "12px", color: MUTED }}><MailOutlined style={{ marginRight: "3px" }} />{contact.email}</span>}
            {contact.phone && <span style={{ fontSize: "12px", color: MUTED }}><PhoneOutlined style={{ marginRight: "3px" }} />{contact.phone}</span>}
            {contact.jobTitle && contact.employer && (
              <span style={{ fontSize: "12px", color: MUTED }}>{contact.jobTitle} @ {contact.employer}</span>
            )}
          </div>
          {/* Signals */}
          {signals.length > 0 && (
            <div style={{ display: "flex", gap: "6px", marginTop: "6px", flexWrap: "wrap" }}>
              {signals.map((s, i) => (
                <span key={i} style={{
                  fontSize: "11px", background: s.priority === "urgent" ? "#FFF5F5" :
                    s.priority === "high" ? "#FFFBF0" : BG,
                  color: s.priority === "urgent" ? RED : s.priority === "high" ? GOLD : NAVY,
                  border: `1px solid ${s.priority === "urgent" ? "#FED7D7" : s.priority === "high" ? "#FDE68A" : BORDER}`,
                  borderRadius: "20px", padding: "2px 8px",
                }}>
                  {s.icon} {s.label}
                </span>
              ))}
            </div>
          )}
          {/* Last contact */}
          {lastContactDays !== null && (
            <div style={{ fontSize: "11px", color: lastContactDays > 90 ? RED : MUTED, marginTop: "4px" }}>
              Last contact: {lastContactDays === 0 ? "Today" : `${lastContactDays}d ago`}
            </div>
          )}
          {/* Hobbies */}
          {contact.hobbies?.length > 0 && (
            <div style={{ marginTop: "6px" }}>
              {contact.hobbies.slice(0, 4).map(h => (
                <Tag key={h} style={{ margin: "2px", borderRadius: "20px", fontSize: "10px" }}>{h}</Tag>
              ))}
            </div>
          )}
        </Col>
        <Col style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-end" }}>
          <Button size="small" type="primary" onClick={() => onContact(contact)}
            style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontSize: "11px" }}>
            Reach Out
          </Button>
          <div style={{ display: "flex", gap: "6px" }}>
            {!contact.inviteSent && !contact.joinedPlatform && contact.email && (
              <Tooltip title="Invite to My Home Buddha">
                <Button size="small" icon={<MailOutlined />} onClick={() => onInvite(contact)}
                  style={{ borderRadius: "50%", borderColor: GREEN, color: GREEN }} />
              </Tooltip>
            )}
            <Tooltip title="Edit">
              <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(contact)}
                style={{ borderRadius: "50%" }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Button size="small" icon={<DeleteOutlined />} danger onClick={() => onDelete(contact)}
                style={{ borderRadius: "50%" }} />
            </Tooltip>
          </div>
        </Col>
      </Row>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reach Out Modal — AI writes the message
// ─────────────────────────────────────────────────────────────────────────────
class ReachOutModal extends Component {
  state = { aiLoading: false, message: "", channel: "email" };

  componentDidMount() { this.generateMessage(); }

  generateMessage = async () => {
    const { contact, agentName } = this.props;
    this.setState({ aiLoading: true });
    const signals = signalBadge(contact);
    const signalContext = signals.map(s => s.label).join(", ");
    const context = [
      contact.notes && `Notes: ${contact.notes}`,
      contact.hobbies?.length && `Hobbies: ${contact.hobbies.join(", ")}`,
      contact.employer && `Works at: ${contact.employer} as ${contact.jobTitle || ""}`,
      contact.spousePartner && `Spouse/partner: ${contact.spousePartner}`,
      contact.children && `Children: ${contact.children}`,
      contact.currentHome && `Home: ${contact.currentHome}`,
      contact.mortgageRate && `Mortgage rate: ${contact.mortgageRate}%`,
      signalContext && `Signals: ${signalContext}`,
    ].filter(Boolean).join(". ");

    try {
      const resp = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 300,
          messages: [{
            role: "user",
            content: `You are a real estate agent assistant. Write a warm, personal, non-salesy reach-out message from ${agentName || "the agent"} to ${contact.firstName} ${contact.lastName || ""}. Keep it conversational and genuine — like a friend reaching out, not a sales pitch. 3-4 sentences max.\n\nContext about ${contact.firstName}: ${context || "No additional context"}\n\nWrite a short ${this.state.channel === "email" ? "email" : "text message"} that feels authentic. Do not mention real estate unless there's a specific signal that warrants it. Focus on the personal connection first.`
          }]
        }),
      });
      const data = await resp.json();
      const text = data?.content?.[0]?.text || "";
      this.setState({ message: text, aiLoading: false });
    } catch(e) {
      this.setState({ aiLoading: false });
    }
  };

  render() {
    const { visible, onClose, contact } = this.props;
    const { aiLoading, message, channel } = this.state;

    return (
      <Modal visible={visible} title={`Reach Out to ${contact?.firstName}`}
        onCancel={onClose} width={520} className="modal-black-close"
        footer={[
          <Button key="copy" onClick={() => { navigator.clipboard?.writeText(message); notification.success({ message: "Copied!" }); }}
            style={{ borderRadius: "50px" }}>Copy Message</Button>,
          <Button key="regen" onClick={this.generateMessage} loading={aiLoading}
            style={{ borderRadius: "50px", borderColor: NAVY, color: NAVY }}>✨ Regenerate</Button>,
          <Button key="close" onClick={onClose} style={{ borderRadius: "50px" }}>Done</Button>
        ]}
        bodyStyle={{ padding: "20px 24px" }}>

        <div style={{ marginBottom: "14px" }}>
          <Select value={channel} onChange={v => this.setState({ channel: v }, this.generateMessage)}
            style={{ width: "160px", marginBottom: "12px" }} size="small" getPopupContainer={t => t.parentNode}>
            <Option value="email">📧 Email</Option>
            <Option value="text">💬 Text Message</Option>
            <Option value="linkedin">💼 LinkedIn DM</Option>
          </Select>
        </div>

        {aiLoading ? (
          <div style={{ textAlign: "center", padding: "30px", color: MUTED }}>
            <Spin /> <div style={{ marginTop: "10px", fontSize: "13px" }}>Writing a personalized message...</div>
          </div>
        ) : (
          <TextArea rows={6} value={message} onChange={e => this.setState({ message: e.target.value })}
            style={{ borderRadius: "10px", fontSize: "14px", lineHeight: 1.7 }} />
        )}
        <div style={{ fontSize: "11px", color: MUTED, marginTop: "8px" }}>
          AI-generated based on what you know about {contact?.firstName}. Edit freely before sending.
        </div>
      </Modal>
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Main ContactManager Component
// ─────────────────────────────────────────────────────────────────────────────
class ContactManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      contacts:        [],
      loading:         true,
      search:          "",
      filterRel:       "all",
      filterSignal:    false,
      // Modals
      showAdd:         false,
      showImport:      false,
      editContact:     null,
      reachOutContact: null,
      deleteContact:   null,
      // Form
      form:            EMPTY_FORM(),
      saving:          false,
    };
  }

  componentDidMount() { this.loadContacts(); }

  loadContacts = async () => {
    const { userDetails } = this.props.context.state;
    if (!userDetails?.id) { this.setState({ loading: false }); return; }
    this.setState({ loading: true });
    try {
      const resp = await list("contacts", { agentId: userDetails.id });
      const contacts = (resp.success ? resp.data || [] : [])
        .filter(c => c.recordType === "agentContact")
        .sort((a, b) => {
          // Sort by signal priority first, then by name
          const aSigs = signalBadge(a).length;
          const bSigs = signalBadge(b).length;
          if (bSigs !== aSigs) return bSigs - aSigs;
          return (a.firstName||"").localeCompare(b.firstName||"");
        });
      this.setState({ contacts, loading: false });
    } catch(e) {
      console.error("ContactManager load:", e);
      this.setState({ loading: false });
    }
  };

  handleSave = async () => {
    const { form, editContact } = this.state;
    const { userDetails } = this.props.context.state;
    if (!form.firstName) { notification.warning({ message: "First name is required" }); return; }
    this.setState({ saving: true });
    const now = new Date().toISOString();
    try {
      const payload = {
        entityData: {
          ...form,
          agentId:    userDetails.id,
          recordType: "agentContact",
          updatedAt:  now,
          ...(editContact ? {} : { createdAt: now, source: "manual" }),
        }
      };
      let result;
      if (editContact?.id) {
        result = await update("contacts", editContact.id, payload);
      } else {
        result = await add("contacts", payload);
      }
      if (result.success) {
        notification.success({ message: editContact ? "Contact updated!" : "Contact added! 🎉" });
        this.setState({ showAdd: false, editContact: null, form: EMPTY_FORM(), saving: false });
        await this.loadContacts();
      } else throw new Error(result.errors);
    } catch(e) {
      notification.error({ message: "Save failed", description: e.message });
      this.setState({ saving: false });
    }
  };

  handleDelete = async () => {
    const { deleteContact } = this.state;
    if (!deleteContact?.id) return;
    try {
      await deleteRecord("contacts", deleteContact.id);
      notification.success({ message: "Contact removed" });
      this.setState({ deleteContact: null });
      await this.loadContacts();
    } catch(e) {
      notification.error({ message: "Delete failed", description: e.message });
    }
  };

  handleInvite = async (contact) => {
    const { userDetails } = this.props.context.state;
    try {
      // Send invitation email via existing Lambda
      await fetch(process.env.REACT_APP_API_GATEWAY_URL + "/sendInvitation", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entityData: {
            firstName:  contact.firstName,
            lastName:   contact.lastName || "",
            email:      contact.email,
            agentName:  `${userDetails.firstName} ${userDetails.lastName}`.trim(),
            agentId:    userDetails.id,
            typeOfUser: "contact",
          }
        })
      });
      // Mark as invited
      await update("contacts", contact.id, {
        entityData: { inviteSent: true, inviteSentAt: new Date().toISOString() }
      });
      notification.success({ message: `Invitation sent to ${contact.firstName}!` });
      await this.loadContacts();
    } catch(e) {
      notification.error({ message: "Invite failed", description: e.message });
    }
  };

  openEdit = (contact) => {
    this.setState({
      editContact: contact,
      form: { ...EMPTY_FORM(), ...contact },
      showAdd: true,
    });
  };

  getFilteredContacts = () => {
    const { contacts, search, filterRel, filterSignal } = this.state;
    return contacts.filter(c => {
      const name = `${c.firstName||""} ${c.lastName||""} ${c.email||""} ${c.employer||""}`.toLowerCase();
      if (search && !name.includes(search.toLowerCase())) return false;
      if (filterRel !== "all" && c.relationshipType !== filterRel) return false;
      if (filterSignal && signalBadge(c).length === 0) return false;
      return true;
    });
  };

  render() {
    const { contacts, loading, search, filterRel, filterSignal,
      showAdd, showImport, editContact, reachOutContact, deleteContact,
      form, saving } = this.state;
    const { userDetails } = this.props.context.state;

    const filtered       = this.getFilteredContacts();
    const totalSignals   = contacts.reduce((s, c) => s + signalBadge(c).length, 0);
    const upcomingBdays  = contacts.filter(c => { const d = daysUntilNextBirthday(c.birthday); return d !== null && d <= 30; }).length;
    const needsContact   = contacts.filter(c => {
      const d = daysSince(c.lastContactDate);
      const freq = c.contactFrequency || "quarterly";
      const thresh = { weekly:7,biweekly:14,monthly:30,quarterly:90,biannual:180,annual:365,as_needed:999 };
      return d !== null && d > (thresh[freq]||90);
    }).length;

    return (
      <div style={{ padding: "16px 0" }}>

        {/* Summary bar */}
        <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
          {[
            { label: "Total Contacts",   value: contacts.length,   color: NAVY  },
            { label: "Active Signals",   value: totalSignals,      color: totalSignals > 0 ? GOLD : MUTED },
            { label: "Birthdays (30d)",  value: upcomingBdays,     color: upcomingBdays > 0 ? "#E53E3E" : MUTED },
            { label: "Needs Follow-Up",  value: needsContact,      color: needsContact > 0 ? GOLD : MUTED },
            { label: "On Platform",      value: contacts.filter(c => c.joinedPlatform).length, color: GREEN },
          ].map(({ label, value, color }) => (
            <Col key={label} xs={12} sm={8} md={4}>
              <Card style={{ borderRadius: "12px", border: `1px solid ${BORDER}`, textAlign: "center" }} bodyStyle={{ padding: "10px 8px" }}>
                <div style={{ fontSize: "20px", fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: "11px", color: MUTED }}>{label}</div>
              </Card>
            </Col>
          ))}
          <Col xs={12} sm={8} md={4}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => this.setState({ showAdd: true, editContact: null, form: EMPTY_FORM() })}
              style={{ width: "100%", height: "52px", borderRadius: "12px", background: NAVY, borderColor: NAVY, fontWeight: 700 }}>
              Add Contact
            </Button>
          </Col>
          <Col xs={12} sm={8} md={4}>
            <Button icon={<ImportOutlined />} onClick={() => this.setState({ showImport: true })}
              style={{ width: "100%", height: "52px", borderRadius: "12px", borderColor: BORDER, fontWeight: 600 }}>
              Import CSV
            </Button>
          </Col>
        </Row>

        {/* Search + filter bar */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "16px", flexWrap: "wrap" }}>
          <Input prefix={<SearchOutlined style={{ color: MUTED }} />}
            placeholder="Search contacts..."
            value={search} onChange={e => this.setState({ search: e.target.value })}
            style={{ borderRadius: "50px", flex: 1, minWidth: "200px" }} allowClear />
          <Select value={filterRel} onChange={v => this.setState({ filterRel: v })}
            style={{ width: "180px" }} getPopupContainer={t => t.parentNode}>
            <Option value="all">All Relationships</Option>
            {RELATIONSHIP_TYPES.map(r => <Option key={r.value} value={r.value}>{r.label}</Option>)}
          </Select>
          <Button icon={<BellOutlined />}
            type={filterSignal ? "primary" : "default"}
            onClick={() => this.setState(p => ({ filterSignal: !p.filterSignal }))}
            style={{ borderRadius: "50px", background: filterSignal ? GOLD : WHITE,
              borderColor: filterSignal ? GOLD : BORDER, color: filterSignal ? "#2A1600" : NAVY, fontWeight: 600 }}>
            Has Signals {filterSignal && `(${contacts.filter(c=>signalBadge(c).length>0).length})`}
          </Button>
        </div>

        {/* Contact list */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ color: MUTED, marginTop: "12px" }}>Loading your contacts...</div>
          </div>
        ) : filtered.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ fontWeight: 700, color: NAVY, marginBottom: "6px" }}>
                  {contacts.length === 0 ? "No contacts yet" : "No contacts match your filter"}
                </div>
                <div style={{ fontSize: "13px", color: MUTED, marginBottom: "16px" }}>
                  {contacts.length === 0
                    ? "Add your first contact manually or import a CSV from your existing database."
                    : "Try adjusting your search or filter."}
                </div>
                {contacts.length === 0 && (
                  <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                    <Button type="primary" icon={<PlusOutlined />}
                      onClick={() => this.setState({ showAdd: true, editContact: null, form: EMPTY_FORM() })}
                      style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY }}>
                      Add First Contact
                    </Button>
                    <Button icon={<ImportOutlined />}
                      onClick={() => this.setState({ showImport: true })}
                      style={{ borderRadius: "50px" }}>
                      Import CSV
                    </Button>
                  </div>
                )}
              </div>
            } />
        ) : (
          filtered.map(c => (
            <ContactCard key={c.id} contact={c}
              onEdit={this.openEdit}
              onDelete={(contact) => this.setState({ deleteContact: contact })}
              onInvite={this.handleInvite}
              onContact={(contact) => this.setState({ reachOutContact: contact })}
            />
          ))
        )}

        {/* Add / Edit Modal */}
        <Modal
          visible={showAdd}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <UserOutlined style={{ color: NAVY }} />
              <span>{editContact ? `Edit ${editContact.firstName}` : "Add New Contact"}</span>
            </div>
          }
          onCancel={() => this.setState({ showAdd: false, editContact: null, form: EMPTY_FORM() })}
          width={680}
          className="modal-black-close"
          footer={[
            <Button key="cancel" onClick={() => this.setState({ showAdd: false, editContact: null, form: EMPTY_FORM() })}
              style={{ borderRadius: "50px" }}>Cancel</Button>,
            <Button key="save" type="primary" loading={saving} onClick={this.handleSave}
              style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY }}>
              {editContact ? "Save Changes" : "Add Contact"}
            </Button>
          ]}
          bodyStyle={{ padding: "20px 24px" }}>
          <ContactForm
            form={form}
            onChange={(field, val) => this.setState(p => ({ form: { ...p.form, [field]: val } }))}
            onAddLifeEvent={() => this.setState(p => ({
              form: { ...p.form, lifeEvents: [...(p.form.lifeEvents||[]), { type: "milestone", date: "", notes: "" }] }
            }))}
            onRemoveLifeEvent={(i) => this.setState(p => ({
              form: { ...p.form, lifeEvents: p.form.lifeEvents.filter((_,idx) => idx !== i) }
            }))}
            onChangeLifeEvent={(i, key, val) => this.setState(p => {
              const evts = [...(p.form.lifeEvents||[])];
              evts[i] = { ...evts[i], [key]: val };
              return { form: { ...p.form, lifeEvents: evts } };
            })}
          />
        </Modal>

        {/* CSV Import Modal */}
        <CSVImportModal
          visible={showImport}
          agentId={userDetails?.id}
          onClose={() => { this.setState({ showImport: false }); this.loadContacts(); }}
          onImported={(count) => { this.loadContacts(); }}
        />

        {/* Reach Out Modal */}
        {reachOutContact && (
          <ReachOutModal
            visible={!!reachOutContact}
            contact={reachOutContact}
            agentName={`${userDetails?.firstName||""} ${userDetails?.lastName||""}`.trim()}
            onClose={() => this.setState({ reachOutContact: null })}
          />
        )}

        {/* Delete confirm */}
        <Modal visible={!!deleteContact}
          title="Remove Contact?"
          onOk={this.handleDelete}
          onCancel={() => this.setState({ deleteContact: null })}
          okText="Remove" okButtonProps={{ danger: true, style: { borderRadius: "50px" } }}
          cancelButtonProps={{ style: { borderRadius: "50px" } }}
          className="modal-black-close">
          <p>Remove <strong>{deleteContact?.firstName} {deleteContact?.lastName}</strong> from your contacts?
          This won't affect them if they've already joined My Home Buddha.</p>
        </Modal>
      </div>
    );
  }
}

export default withAppContext(ContactManager);